import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { Button } from 'components/Elements/Buttons/Button';
import { FormInput } from 'components/Elements/Inputs/FormInput';
import { RecaptchaChallenge } from 'components/Elements/Inputs/RecaptchaChallenge';
import { formatMaskedEmail } from 'utils/formatMaskedEmail';

const EMAIL_MAX_LENGTH = 100;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_REQUIRED = 'A valid email address is required.';
const EMAIL_TOO_LONG = 'Email address is too long.';

const newEmailRules = {
  required: EMAIL_REQUIRED,
  maxLength: { value: EMAIL_MAX_LENGTH, message: EMAIL_TOO_LONG },
  pattern: { value: EMAIL_PATTERN, message: EMAIL_REQUIRED },
};

const subtitleSx = {
  fontWeight: 700,
  color: 'primary.main',
  fontSize: { xs: 20, md: 22 },
} as const;

const buttonFontSx = {
  fontWeight: 700,
} as const;

interface EmailChangeForm {
  newEmail: string;
}

export interface EmailChangeDialogProps {
  open: boolean;
  physicianName?: string;
  maskedEmail?: string | null;
  loading?: boolean;
  successMessage?: string | null;
  fieldError?: string;
  formError?: string;
  formErrorSeverity?: 'error' | 'warning';
  onClose: () => void;
  onExited?: () => void;
  onSubmit: (newEmail: string, recaptchaToken: string) => void;
}

export const EmailChangeDialog: React.FC<EmailChangeDialogProps> = ({
  open,
  physicianName,
  maskedEmail,
  loading = false,
  successMessage,
  fieldError,
  formError,
  formErrorSeverity = 'error',
  onClose,
  onExited,
  onSubmit,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  // The solved token. The button stays disabled until the widget reports one.
  const [captchaToken, setCaptchaToken] = React.useState('');
  // Enter submits the form while the button is still disabled, so this prompt has to be remembered.
  const [captchaIncomplete, setCaptchaIncomplete] = React.useState(false);
  // The address from the failed request. The server's email error hides once that text changes.
  const [submittedEmail, setSubmittedEmail] = React.useState<string | null>(null);
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);
  const { control, handleSubmit, reset, watch, formState, setFocus } = useForm<EmailChangeForm>({
    defaultValues: { newEmail: '' },
  });
  const enteredEmail = watch('newEmail');
  const currentEmail = formatMaskedEmail(maskedEmail);
  const clientMessage = typeof formState.errors.newEmail?.message === 'string' ? formState.errors.newEmail.message : undefined;
  const showServerFieldError = Boolean(fieldError) && enteredEmail === submittedEmail;
  const visibleFieldError = clientMessage || (showServerFieldError ? fieldError : undefined);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleExited = () => {
    reset();
    setCaptchaToken('');
    setCaptchaIncomplete(false);
    setSubmittedEmail(null);
    onExited?.();
  };

  React.useLayoutEffect(() => {
    if (open && successMessage) {
      closeButtonRef.current?.focus();
    }
  }, [open, successMessage]);

  React.useEffect(() => {
    if (open && showServerFieldError) {
      setFocus('newEmail');
    }
  }, [open, showServerFieldError, setFocus]);

  const handleTokenChange = (token: string) => {
    setCaptchaToken(token);
    if (token) {
      setCaptchaIncomplete(false);
    }
  };

  const handleValidSubmit = (values: EmailChangeForm) => {
    if (loading) return;
    if (!captchaToken) {
      setCaptchaIncomplete(true);
      return;
    }

    const token = captchaToken;
    const newEmail = values.newEmail.trim();
    // Drop the token so the button disables immediately. Leave the solved checkbox on screen until the request fails.
    setCaptchaToken('');
    setCaptchaIncomplete(false);
    setSubmittedEmail(newEmail);
    onSubmit(newEmail, token);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="email-change-dialog-title"
      aria-describedby={successMessage ? undefined : 'email-change-dialog-description'}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      TransitionProps={{ onExited: handleExited }}
    >
      <DialogTitle id="email-change-dialog-title" variant="h4" sx={subtitleSx}>
        {successMessage ? 'Confirmation sent' : 'Edit email address'}
      </DialogTitle>
      <DialogContent>
        {successMessage ? (
          <Alert severity="success">{successMessage}</Alert>
        ) : (
          <Box component="form" noValidate onSubmit={handleSubmit(handleValidSubmit)} sx={{ position: 'relative' }}>
            <DialogContentText id="email-change-dialog-description" sx={{ mb: 2, color: 'text.primary' }}>
              Future assessment statements will be sent to this address after you confirm the link sent to the new inbox. The address on
              file does not change until that link is opened.
            </DialogContentText>
            <Box
              sx={{
                mb: 2,
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              {physicianName && (
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: 'grey.100',
                    borderBottom: '1px solid',
                    borderColor: 'grey.300',
                  }}
                >
                  <Typography fontWeight={700} color="primary.main">
                    {physicianName}
                  </Typography>
                </Box>
              )}
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Email on file
                </Typography>
                <Typography>{currentEmail}</Typography>
              </Box>
            </Box>
            <FormInput
              control={control}
              name="newEmail"
              rules={newEmailRules}
              label="New email address"
              color="primary"
              variant="outlined"
              size="small"
              fullWidth
              type="email"
              required
              autoComplete="email"
              disabled={loading}
              error={Boolean(visibleFieldError)}
              helperText={visibleFieldError}
            />
            {formError && (
              <Alert severity={formErrorSeverity} sx={{ mt: 2 }}>
                {formError}
              </Alert>
            )}
            <Box
              // Block clicks while the email field is disabled so the solved checkbox cannot be cleared mid-request.
              data-testid="captcha-challenge"
              aria-disabled={loading}
              sx={{
                mt: 2,
                pointerEvents: loading ? 'none' : 'auto',
              }}
            >
              <RecaptchaChallenge
                open
                // False while loading, so the checkbox is not cleared until the fields are usable again.
                rejected={!loading && Boolean(fieldError || formError)}
                onTokenChange={handleTokenChange}
                promptIncomplete={captchaIncomplete}
              />
            </Box>
            <Box
              component="button"
              type="submit"
              tabIndex={-1}
              aria-hidden
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '1px',
                height: '1px',
                p: 0,
                m: 0,
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {successMessage ? (
          <Button
            ref={closeButtonRef}
            size="medium"
            variant="contained"
            color="primary"
            onClick={handleClose}
            sx={{
              ...buttonFontSx,
              bgcolor: 'primary.main',
              color: '#FFFFFF',
              border: '1px solid',
              borderColor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.main',
                borderColor: 'primary.main',
              },
            }}
          >
            Close
          </Button>
        ) : (
          <>
            <Button
              size="medium"
              variant="outlined"
              color="primary"
              onClick={handleClose}
              disabled={loading}
              sx={{
                ...buttonFontSx,
                bgcolor: '#FFFFFF',
                color: 'primary.main',
                borderColor: 'primary.main',
                '&:hover': {
                  bgcolor: '#FFFFFF',
                  borderColor: 'primary.main',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              size="medium"
              variant="contained"
              color="primary"
              type="button"
              onClick={handleSubmit(handleValidSubmit)}
              disabled={loading || !captchaToken}
              aria-busy={loading}
              sx={{
                ...buttonFontSx,
                bgcolor: 'primary.main',
                color: '#FFFFFF',
                border: '1px solid',
                borderColor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.main',
                  borderColor: 'primary.main',
                },
              }}
            >
              {loading && <CircularProgress aria-hidden size={16} color="inherit" sx={{ mr: 1 }} />}
              Send confirmation
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
